import { useState, useEffect, useCallback } from 'react';
import { Plus, Trash2 } from 'lucide-react';
import { Button } from '../../../../foundation/altzero_base/base/components/ui/button';
import { Input } from '../../../../foundation/altzero_base/base/components/ui/input';
import { Label } from '../../../../foundation/altzero_base/base/components/ui/label';
import { ContractDrawer } from './ContractDrawer';
import { dmaContractService } from '../../services/contractService';
import type { BvDma, BvDmaFormData } from '../../types/billValidationTypes';

type Props = { siteId: string };

const EMPTY: BvDmaFormData = { dma_provider: '' };

export function DmaContractSection({ siteId }: Props) {
  const [contracts, setContracts] = useState<BvDma[]>([]);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editing, setEditing] = useState<BvDma | null>(null);
  const [form, setForm] = useState<BvDmaFormData>(EMPTY);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    const r = await dmaContractService.listBySite(siteId);
    if (r.success) setContracts(r.data ?? []);
  }, [siteId]);

  useEffect(() => { load(); }, [load]);

  const set = <K extends keyof BvDmaFormData>(k: K, v: BvDmaFormData[K]) =>
    setForm((f) => ({ ...f, [k]: v }));

  const openAdd = () => { setEditing(null); setForm(EMPTY); setDrawerOpen(true); };

  const openEdit = (c: BvDma) => {
    setEditing(c);
    setForm({ dma_provider: c.dma_provider ?? '', start_date: c.start_date, end_date: c.end_date });
    setDrawerOpen(true);
  };

  const save = async () => {
    if (!form.dma_provider?.trim()) { setError('Provider is required'); return; }
    setSaving(true);
    setError(null);
    const r = editing
      ? await dmaContractService.update(editing.id, form)
      : await dmaContractService.create(siteId, form);
    if (!r.success) { setError(r.error ?? 'Save failed'); setSaving(false); return; }
    await load();
    setDrawerOpen(false);
    setSaving(false);
  };

  const remove = async (id: string) => {
    if (!confirm('Delete this DMA record?')) return;
    await dmaContractService.delete(id);
    await load();
  };

  const fmtDate = (d?: string) => d ? new Date(d).toLocaleDateString('en-AU') : '—';

  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-semibold text-gray-700">DMA</h3>
        <Button size="sm" variant="outline" onClick={openAdd}>
          <Plus className="h-3.5 w-3.5 mr-1" />
          Add DMA
        </Button>
      </div>

      {contracts.length === 0 ? (
        <p className="text-sm text-gray-400 py-2">No DMA records</p>
      ) : (
        <div className="space-y-2">
          {contracts.map((c) => (
            <div key={c.id} className="border border-gray-200 rounded-lg px-3 py-2.5">
              <div className="flex items-start justify-between">
                <div onClick={() => openEdit(c)} className="cursor-pointer">
                  <span className="text-sm font-medium text-gray-700">{c.dma_provider || '—'}</span>
                  <p className="text-xs text-gray-400 mt-0.5">
                    {fmtDate(c.start_date)} → {fmtDate(c.end_date)}
                  </p>
                </div>
                <button onClick={() => remove(c.id)} className="p-1 hover:text-red-600">
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      <ContractDrawer open={drawerOpen} onClose={() => setDrawerOpen(false)} title={editing ? 'Edit DMA' : 'Add DMA'}>
        <div className="space-y-4">
          <div>
            <Label>DMA Provider *</Label>
            <Input value={form.dma_provider ?? ''} onChange={(e) => set('dma_provider', e.target.value)} placeholder="e.g. Powermetric" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Start Date</Label>
              <Input type="date" value={form.start_date ?? ''} onChange={(e) => set('start_date', e.target.value)} />
            </div>
            <div>
              <Label>End Date <span className="text-gray-400 font-normal">(max 65 mo)</span></Label>
              <Input type="date" value={form.end_date ?? ''} onChange={(e) => set('end_date', e.target.value)} />
            </div>
          </div>
          {error && <p className="text-sm text-red-600">{error}</p>}
          <div className="flex gap-2 pt-2">
            <Button variant="outline" className="flex-1" onClick={() => setDrawerOpen(false)}>Cancel</Button>
            <Button className="flex-1" onClick={save} disabled={saving}>{saving ? 'Saving…' : 'Save'}</Button>
          </div>
        </div>
      </ContractDrawer>
    </div>
  );
}
